import React, { useContext, useEffect, useState } from "react"
import { defaultConfig, DynamicConfig, IdpProvider } from "./config"
import { dynamicConfigUrl, idpConfigUrl } from "./config"

interface DynamicConfigContext {
  config: DynamicConfig
  setConfig?: (newConfig: DynamicConfig) => void
  isTestEnvironment: boolean
}

const configContextObject = React.createContext<DynamicConfigContext>({
  config: defaultConfig,
  isTestEnvironment: false
});

export const useConfig = () => useContext(configContextObject);

const configLoadingErrorMessage =
  "Error while fetching global config, the App will not be rendered. (This is NOT a React error.)"

type Props = {
  children: React.ReactNode;
}

const ConfigContextProvider = ({ children }: Props) => {
  const [configState, setConfigState] = useState(defaultConfig);
  const [isTestEnvironmentState, setIsTestEnvironment] = useState(false)
  const [configLoadingState, setConfigLoadingState] = useState<
    "loading" | "ready" | "error"
  >("loading")

  useEffect(() => {
    setIsTestEnvironment(configState?.accountType === "test")
  }, [configState])

  useEffect( () => {
    const fetchConfig = async() => {
      const configResponse = await fetch(dynamicConfigUrl)
      if (!configResponse.ok) {
        throw new Error(`Failed to fetch config: ${configResponse.statusText}`)
      }
      const config = await configResponse.json() as DynamicConfig

      const idps = await fetch(idpConfigUrl)
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`Failed to fetch IDP config: ${response.statusText}`)
          }
          return response.json() as Promise<IdpProvider[]>
        })
        .catch(() => {
          return []
        // just ignore this error since IdP config is optional
      })
      setConfigState({...config, idpProviders: idps})
      setConfigLoadingState("ready")
    }

    fetchConfig().catch((e) => {
      // eslint-disable-next-line no-console
      console.log(
        configLoadingErrorMessage,
        `Have you provided the config file '${dynamicConfigUrl}'?`,
        e
      )
      setConfigLoadingState("error")
        })
  }, [setConfigState])

  if (configLoadingState === "loading") {
    return null
  }
  if (configLoadingState === "error") {
    return (
      <p style={{ color: "red", textAlign: "center" }}>
        {configLoadingErrorMessage}
      </p>
    )
  }

  return (
    <configContextObject.Provider
      value={{
        config: configState,
        setConfig: setConfigState,
        isTestEnvironment: isTestEnvironmentState
      }}
    >
      {children}
    </configContextObject.Provider>
  );
};

export default ConfigContextProvider;
